// Admin login. Single owner/support form. On success redirect to /.
// If user is already authenticated (e.g. returned to /login URL while session
// is still active via refresh cookie), skip the form and redirect immediately.

import { useState } from 'react';
import { Button, Card, Form, Input, Typography, message } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Navigate, useNavigate } from 'react-router-dom';
import { ROUTES } from '../config/routes';
import { useAuth } from '../features/auth/useAuth';

const { Title, Paragraph } = Typography;

interface FormValues {
  username: string;
  password: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated, isHydrated } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  // Wait for hydrate to finish before deciding — otherwise we'd briefly show
  // the login form even when the user has a valid refresh cookie.
  if (!isHydrated) return null;
  if (isAuthenticated) return <Navigate to={ROUTES.dashboard} replace />;

  async function handleSubmit(values: FormValues) {
    setSubmitting(true);
    try {
      await login(values);
      message.success('Đăng nhập thành công');
      navigate(ROUTES.dashboard, { replace: true });
    } catch (err) {
      const apiMessage = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      message.error(apiMessage ?? 'Đăng nhập thất bại');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f0f2f5' }}>
      <Card style={{ width: 380 }}>
        <Title level={3} style={{ marginTop: 0, textAlign: 'center' }}>VR360 License Admin</Title>
        <Paragraph type="secondary" style={{ textAlign: 'center' }}>
          Đăng nhập để quản lý license
        </Paragraph>
        <Form<FormValues> layout="vertical" onFinish={(v) => void handleSubmit(v)}>
          <Form.Item name="username" rules={[{ required: true, message: 'Vui lòng nhập username' }]}>
            <Input prefix={<UserOutlined />} placeholder="Username" autoFocus autoComplete="username" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: 'Vui lòng nhập password' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Password" autoComplete="current-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={submitting}>
            Đăng nhập
          </Button>
        </Form>
      </Card>
    </div>
  );
}
